import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { Can, usePermissions } from '../../contexts/PermissionContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePendingWork } from '../../contexts/PendingWorkContext';
import './Admin.css';

function AdminDashboard() {
    const { hasAnyPermission } = usePermissions();
    const { user, isAdmin } = useAuth();
    // ตัวเลขงานรอดำเนินการบนการ์ด — มาจาก PendingWorkContext (รอบเดียว, อัปเดต realtime)
    // แทนการยิง 4 endpoint แยกตอน mount ซึ่งค้างค่าเดิมจนกว่าจะรีเฟรชหน้า
    const { counts: pendingCounts } = usePendingWork();
    const docPendingCount = pendingCounts.doc_pending || 0;
    const accountReqCount = (pendingCounts.account_pending || 0) + (pendingCounts.account_awaiting || 0);
    const passwordResetCount = pendingCounts.password_reset || 0;
    const positionReqCount = pendingCounts.position_pending || 0;
    const contactCount = pendingCounts.contact_new || 0;
    const canViewUsers = hasAnyPermission('users.view', 'users.edit');
    // บัญชีดำ — เช็คจาก permission ที่ถูก grant รายคนจริง ๆ ไม่ใช้ hasPermission (ซึ่ง bypass ให้ admin ทุกคน)
    const canManageBlacklist = !!user?.permissions?.includes('banned_cid.manage');
    const [stats, setStats] = useState(null);
    const [requestStats, setRequestStats] = useState(null);
    const [recentLogs, setRecentLogs] = useState([]);
    const [loading, setLoading] = useState(true);

    // Modal รายชื่อผู้ที่ยืนยันอีเมลแล้ว
    const [emailModalOpen, setEmailModalOpen] = useState(false);
    const [verifiedUsers, setVerifiedUsers] = useState([]);
    const [loadingVerified, setLoadingVerified] = useState(false);
    const [verifiedSearch, setVerifiedSearch] = useState('');

    useEffect(() => {
        fetchStats();
        fetchRequestStats();
        fetchRecentLogs();
    }, []);

    const fetchStats = async () => {
        try {
            const response = await api.get('/admin/stats');
            setStats(response.data);
        } catch (err) {
            console.error('Error fetching stats:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchRequestStats = async () => {
        try {
            const response = await api.get('/request/admin/stats');
            setRequestStats(response.data);
        } catch (err) {
            console.error('Error fetching request stats:', err);
        }
    };

    const fetchRecentLogs = async () => {
        try {
            const response = await api.get('/logs/recent?limit=10');
            setRecentLogs(response.data);
        } catch (err) {
            console.error('Error fetching recent logs:', err);
        }
    };

    const openEmailModal = async () => {
        setEmailModalOpen(true);
        setVerifiedSearch('');
        setLoadingVerified(true);
        try {
            const response = await api.get('/admin/verified-email-users');
            setVerifiedUsers(response.data.users || []);
        } catch (err) {
            console.error('Error fetching verified email users:', err);
            setVerifiedUsers([]);
        } finally {
            setLoadingVerified(false);
        }
    };

    const getActionBadge = (action) => {
        const badges = {
            login_success: { class: 'success', text: 'เข้าสู่ระบบ', icon: '→' },
            login_failed: { class: 'danger', text: 'ล้มเหลว', icon: '✕' },
            logout: { class: 'secondary', text: 'ออกจากระบบ', icon: '←' },
            request_created: { class: 'info', text: 'สร้างคำร้อง', icon: '+' },
            request_cancelled: { class: 'warning', text: 'ยกเลิกคำร้อง', icon: '○' },
            request_approved: { class: 'success', text: 'อนุมัติ', icon: '✓' },
            request_rejected: { class: 'danger', text: 'ปฏิเสธ', icon: '✕' },
            request_completed: { class: 'primary', text: 'เสร็จสิ้น', icon: '★' }
        };
        return badges[action] || { class: 'secondary', text: action, icon: '•' };
    };

    const formatTime = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);

        if (diffMins < 1) return 'เมื่อสักครู่';
        if (diffMins < 60) return `${diffMins} นาทีที่แล้ว`;
        if (diffHours < 24) return `${diffHours} ชั่วโมงที่แล้ว`;

        return date.toLocaleDateString('th-TH', {
            day: 'numeric',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="flex-center" style={{ padding: '60px 0' }}>
                <div className="spinner"></div>
            </div>
        );
    }

    return (
        <div className="admin-page">
            <div className="admin-header">
                <h1>Dashboard</h1>
                <p>ภาพรวมระบบ iMoney</p>
            </div>

            {/* Quick Menu - ด้านบน */}
            <div className="quick-menu-grid">
                {/* คำร้องขอเอกสาร - อันดับแรก */}
                <Can permissions={['request.view_all', 'request.process', 'request.approve']}>
                    <Link to="/admin/requests" className="quick-menu-card requests">
                        <div className="quick-menu-icon">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
                            </svg>
                        </div>
                        <div className="quick-menu-content">
                            <h3>คำร้องขอเอกสาร</h3>
                            <p>อนุมัติ/ปฏิเสธคำร้องขอสลิปและ 50 ทวิ</p>
                        </div>
                        {docPendingCount > 0 && (
                            <div className="quick-menu-badge pulse">{docPendingCount}</div>
                        )}
                    </Link>
                </Can>

                {/* คำขอเปิดบัญชี */}
                <Can permissions={['account_request.manage']}>
                    <Link to="/admin/account-requests" className="quick-menu-card requests">
                        <div className="quick-menu-icon">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M15 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm-9-2V7H4v3H1v2h3v3h2v-3h3v-2H6zm9 4c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                            </svg>
                        </div>
                        <div className="quick-menu-content">
                            <h3>คำขอเปิดบัญชี</h3>
                            <p>อนุมัติคำขอเปิดบัญชีจากผู้ใช้ใหม่</p>
                        </div>
                        {accountReqCount > 0 && (
                            <div className="quick-menu-badge pulse">{accountReqCount}</div>
                        )}
                    </Link>
                </Can>

                {/* คำขอรีเซ็ตรหัสผ่าน */}
                <Can permissions={['account_request.manage']}>
                    <Link to="/admin/password-resets" className="quick-menu-card requests">
                        <div className="quick-menu-icon">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12.65 10A5.99 5.99 0 0 0 7 6c-3.31 0-6 2.69-6 6s2.69 6 6 6a5.99 5.99 0 0 0 5.65-4H17v4h4v-4h2v-4H12.65zM7 14c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z"/>
                            </svg>
                        </div>
                        <div className="quick-menu-content">
                            <h3>คำขอรีเซ็ตรหัสผ่าน</h3>
                            <p>ตรวจหลักฐานแล้วเปลี่ยนรหัสผ่านให้ผู้ใช้</p>
                        </div>
                        {passwordResetCount > 0 && (
                            <div className="quick-menu-badge pulse">{passwordResetCount}</div>
                        )}
                    </Link>
                </Can>

                {/* ข้อความจากผู้ใช้ */}
                <Can permissions={['request.view_all', 'request.process', 'users.view']}>
                    <Link to="/admin/contact" className="quick-menu-card chat">
                        <div className="quick-menu-icon">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z"/>
                            </svg>
                        </div>
                        <div className="quick-menu-content">
                            <h3>ข้อความจากผู้ใช้</h3>
                            <p>ตอบกลับข้อความที่ผู้ใช้ส่งเข้ามา (ทางอีเมล)</p>
                        </div>
                        {contactCount > 0 && (
                            <div className="quick-menu-badge pulse">{contactCount}</div>
                        )}
                    </Link>
                </Can>

                {/* คำขอแก้ไขชื่อตำแหน่ง */}
                <Can permissions={['position_request.manage', 'users.view', 'users.edit']}>
                    <Link to="/admin/position-requests" className="quick-menu-card requests">
                        <div className="quick-menu-icon">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M20 6h-4V4c0-1.1-.9-2-2-2h-4c-1.1 0-2 .9-2 2v2H4c-1.1 0-2 .9-2 2v11c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm-6 0h-4V4h4v2z"/>
                            </svg>
                        </div>
                        <div className="quick-menu-content">
                            <h3>คำขอแก้ไขชื่อตำแหน่ง</h3>
                            <p>ตรวจสอบและยืนยันชื่อตำแหน่งของผู้ใช้</p>
                        </div>
                        {positionReqCount > 0 && (
                            <div className="quick-menu-badge pulse">{positionReqCount}</div>
                        )}
                    </Link>
                </Can>

                {/* ระบบแชท - ปิดชั่วคราว (ใช้ไม่ได้) ปลดปิดได้โดยเปลี่ยน false เป็น true */}
                {false && (
                <Can permissions={['chat.admin', 'chat.view_all']}>
                    <Link to="/admin/chat" className="quick-menu-card chat">
                        <div className="quick-menu-icon">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"/>
                            </svg>
                        </div>
                        <div className="quick-menu-content">
                            <h3>ระบบแชท</h3>
                            <p>สนทนากับผู้ใช้งานระบบ</p>
                        </div>
                    </Link>
                </Can>
                )}

                {/* รายงานสถิติ */}
                <Can permissions={['view_reports', 'admin_access']}>
                    <Link to="/admin/reports" className="quick-menu-card reports">
                        <div className="quick-menu-icon">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/>
                            </svg>
                        </div>
                        <div className="quick-menu-content">
                            <h3>รายงานสถิติ</h3>
                            <p>สถิติการใช้งานระบบอัตโนมัติ</p>
                        </div>
                    </Link>
                </Can>

                {/* รายงานการใช้งานรายวัน */}
                <Can permissions={['view_reports', 'admin_access']}>
                    <Link to="/admin/usage-report" className="quick-menu-card reports">
                        <div className="quick-menu-icon">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm4 4h14v-2H7v2zm0 4h14v-2H7v2zM7 7v2h14V7H7z"/>
                            </svg>
                        </div>
                        <div className="quick-menu-content">
                            <h3>รายงานการใช้งานรายวัน</h3>
                            <p>ผู้เข้าใช้ระบบ การพิมพ์/ส่งอีเมล + แจ้ง Line 18:00</p>
                        </div>
                    </Link>
                </Can>

                {/* ประวัติการแชร์สลิป */}
                <Can permissions={['view_reports', 'admin_access']}>
                    <Link to="/admin/share-logs" className="quick-menu-card reports">
                        <div className="quick-menu-icon">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z"/>
                            </svg>
                        </div>
                        <div className="quick-menu-content">
                            <h3>ประวัติการแชร์สลิป</h3>
                            <p>ใครแชร์สลิปผ่าน LINE / Facebook / IG / TikTok และแสดงข้อมูลอะไร</p>
                        </div>
                    </Link>
                </Can>

                {/* ยอดสหกรณ์ออมทรัพย์ที่สมาชิกบันทึก — วางในภาพรวมให้หาง่าย เฉพาะ role admin/super_admin */}
                {isAdmin() && (
                    <Link to="/admin/coop-savings" className="quick-menu-card coop">
                        <div className="quick-menu-icon">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M21 18v1c0 1.1-.9 2-2 2H5c-1.11 0-2-.9-2-2V5c0-1.1.89-2 2-2h14c1.1 0 2 .9 2 2v1h-9c-1.11 0-2 .9-2 2v8c0 1.1.89 2 2 2h9zm-9-2h10V8H12v8zm4-2.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
                            </svg>
                        </div>
                        <div className="quick-menu-content">
                            <h3>ยอดสหกรณ์ออมทรัพย์</h3>
                            <p>หุ้น + เงินฝากออมทรัพย์พิเศษ ที่สมาชิกบันทึกไว้</p>
                        </div>
                    </Link>
                )}
            </div>

            {/* Stats Cards */}
            <div className="stats-grid">
                <div className="stat-card">
                    <div className="stat-icon users">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
                        </svg>
                    </div>
                    <div className="stat-content">
                        <span className="stat-value">{stats?.totalUsers || 0}</span>
                        <span className="stat-label">ผู้ใช้ทั้งหมด</span>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-icon active">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                        </svg>
                    </div>
                    <div className="stat-content">
                        <span className="stat-value">{stats?.activeUsers || 0}</span>
                        <span className="stat-label">ผู้ใช้ที่ใช้งาน</span>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-icon sessions">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
                        </svg>
                    </div>
                    <div className="stat-content">
                        <span className="stat-value">{stats?.activeSessions || 0}</span>
                        <span className="stat-label">Sessions ใช้งาน</span>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-icon employees">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M20 6h-4V4c0-1.11-.89-2-2-2h-4c-1.11 0-2 .89-2 2v2H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-6 0h-4V4h4v2z"/>
                        </svg>
                    </div>
                    <div className="stat-content">
                        <span className="stat-value">{stats?.totalEmployees?.toLocaleString() || 0}</span>
                        <span className="stat-label">พนักงานในระบบเงินเดือน</span>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-icon today">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
                        </svg>
                    </div>
                    <div className="stat-content">
                        <span className="stat-value">{stats?.todayLogins || 0}</span>
                        <span className="stat-label">เข้าสู่ระบบวันนี้</span>
                    </div>
                </div>

                {/* Email Verified Card — คลิกเพื่อดูรายชื่อผู้ที่ยืนยันอีเมลแล้ว (เฉพาะผู้มีสิทธิ์ดูข้อมูลผู้ใช้) */}
                <div
                    className={`stat-card${canViewUsers ? ' clickable' : ''}`}
                    onClick={canViewUsers ? openEmailModal : undefined}
                    role={canViewUsers ? 'button' : undefined}
                    tabIndex={canViewUsers ? 0 : undefined}
                    onKeyDown={canViewUsers ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openEmailModal(); } } : undefined}
                    title={canViewUsers ? 'คลิกเพื่อดูรายชื่อผู้ที่ยืนยันอีเมลแล้ว' : undefined}
                >
                    <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}>
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M22 6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h8.11c-.59-.57-1.07-1.25-1.42-2H4V8l8 5 8-5v3.08c.71.1 1.38.31 2 .6V6zm-10 5L4 6h16l-8 5zm7 0c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm-.75 6.25L14.5 15l.75-.75 1 1 2.25-2.25.75.75-3 3z"/>
                        </svg>
                    </div>
                    <div className="stat-content">
                        <span className="stat-value">{stats?.verifiedEmails || 0}</span>
                        <span className="stat-label">ยืนยันอีเมลแล้ว</span>
                        {(stats?.usersWithEmail || 0) > 0 && (
                            <span className="stat-sublabel" style={{ fontSize: 12, color: 'var(--steam-text-muted, #94a3b8)', marginTop: 2 }}>
                                {Math.round(((stats?.verifiedEmails || 0) / stats.usersWithEmail) * 100)}% จาก {stats.usersWithEmail} บัญชีที่มีอีเมล
                            </span>
                        )}
                    </div>
                </div>

                {/* Pending Requests Card */}
                {docPendingCount > 0 && (
                    <Link to="/admin/requests" className="stat-card clickable" style={{ textDecoration: 'none' }}>
                        <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
                            </svg>
                        </div>
                        <div className="stat-content">
                            <span className="stat-value" style={{ color: '#f59e0b' }}>{docPendingCount}</span>
                            <span className="stat-label">คำร้องรอดำเนินการ</span>
                        </div>
                        <div className="stat-badge pulse">ใหม่</div>
                    </Link>
                )}

                {/* Today Requests */}
                {requestStats && (
                    <div className="stat-card">
                        <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)' }}>
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
                            </svg>
                        </div>
                        <div className="stat-content">
                            <span className="stat-value">{requestStats.today_requests || 0}</span>
                            <span className="stat-label">คำร้องวันนี้</span>
                        </div>
                    </div>
                )}
            </div>

            {/* Management Menu */}
            <div className="admin-section">
                <h2>เมนูจัดการ</h2>
                <div className="admin-menu-grid">
                    <Can permission="users.view">
                        <Link to="/admin/users" className="admin-menu-card">
                            <div className="menu-icon">
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
                                </svg>
                            </div>
                            <div className="menu-text">
                                <h3>จัดการผู้ใช้</h3>
                                <p>ผู้ใช้ทั่วไป (login ด้วยเลขบัตร)</p>
                            </div>
                        </Link>
                    </Can>

                    <Can permission="users.view">
                        <Link to="/admin/staff" className="admin-menu-card">
                            <div className="menu-icon" style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)' }}>
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                                </svg>
                            </div>
                            <div className="menu-text">
                                <h3>จัดการเจ้าหน้าที่</h3>
                                <p>เจ้าหน้าที่ระบบ (login ด้วย username)</p>
                            </div>
                        </Link>
                    </Can>

                    <Can permission="roles.view">
                        <Link to="/admin/roles" className="admin-menu-card">
                            <div className="menu-icon" style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}>
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
                                </svg>
                            </div>
                            <div className="menu-text">
                                <h3>จัดการ Roles & Permissions</h3>
                                <p>กำหนดสิทธิ์การเข้าถึงระบบตามตำแหน่ง</p>
                            </div>
                        </Link>
                    </Can>

                    <Can permission="settings.view">
                        <Link to="/admin/settings" className="admin-menu-card">
                            <div className="menu-icon" style={{ background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)' }}>
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M19.14 12.94c.04-.31.06-.63.06-.94 0-.31-.02-.63-.06-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.04.31-.06.63-.06.94s.02.63.06.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z"/>
                                </svg>
                            </div>
                            <div className="menu-text">
                                <h3>ตั้งค่าระบบ</h3>
                                <p>ตั้งค่าทั่วไป และการเชื่อมต่อฐานข้อมูล</p>
                            </div>
                        </Link>
                    </Can>

                    <Can permission="settings.report">
                        <Link to="/admin/report-settings" className="admin-menu-card">
                            <div className="menu-icon" style={{ background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)' }}>
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 2l5 5h-5V4zM6 20V4h5v7h7v9H6zm2-9h8v2H8v-2zm0 4h8v2H8v-2z"/>
                                </svg>
                            </div>
                            <div className="menu-text">
                                <h3>ตั้งค่ารายงาน</h3>
                                <p>แก้ไขข้อความในสลิปและ 50 ทวิ</p>
                            </div>
                        </Link>
                    </Can>

                    <Can permission="settings.edit">
                        <Link to="/admin/signatures" className="admin-menu-card">
                            <div className="menu-icon" style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)' }}>
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M20.71 7.04c.39-.39.39-1.04 0-1.41l-2.34-2.34c-.37-.39-1.02-.39-1.41 0l-1.84 1.83 3.75 3.75M3 17.25V21h3.75L17.81 9.93l-3.75-3.75L3 17.25z"/>
                                </svg>
                            </div>
                            <div className="menu-text">
                                <h3>ตั้งค่าลายเซ็น</h3>
                                <p>กำหนดลายเซ็นสำหรับเอกสาร</p>
                            </div>
                        </Link>
                    </Can>

                    <Can permission="logs.view">
                        <Link to="/admin/print-history" className="admin-menu-card">
                            <div className="menu-icon" style={{ background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)' }}>
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M19 8H5c-1.66 0-3 1.34-3 3v6h4v4h12v-4h4v-6c0-1.66-1.34-3-3-3zm-3 11H8v-5h8v5zm3-7c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm-1-9H6v4h12V3z"/>
                                </svg>
                            </div>
                            <div className="menu-text">
                                <h3>ประวัติการพิมพ์เอกสาร</h3>
                                <p>ติดตามการพิมพ์สลิปและ 50 ทวิ</p>
                            </div>
                        </Link>
                    </Can>

                    <Can permission="logs.view">
                        <Link to="/admin/activity-logs" className="admin-menu-card">
                            <div className="menu-icon" style={{ background: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)' }}>
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M13 3c-4.97 0-9 4.03-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42C8.27 19.99 10.51 21 13 21c4.97 0 9-4.03 9-9s-4.03-9-9-9zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12z"/>
                                </svg>
                            </div>
                            <div className="menu-text">
                                <h3>Activity Logs</h3>
                                <p>ดูประวัติการใช้งานระบบ</p>
                            </div>
                        </Link>
                    </Can>

                    <Can permissions={['settings.edit', 'system.maintenance']}>
                        <Link to="/admin/maintenance" className="admin-menu-card">
                            <div className="menu-icon" style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M22.7 19l-9.1-9.1c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1.1.1-1.4z"/>
                                </svg>
                            </div>
                            <div className="menu-text">
                                <h3>ปิดปรับปรุงระบบ</h3>
                                <p>ตั้งค่าปิดระบบและประกาศผู้ใช้</p>
                            </div>
                        </Link>
                    </Can>

                    <Can permissions={['settings.edit', 'request.process']}>
                        <Link to="/admin/payment-dates" className="admin-menu-card">
                            <div className="menu-icon" style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)' }}>
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM9 10H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm-8 4H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2z"/>
                                </svg>
                            </div>
                            <div className="menu-text">
                                <h3>กำหนดวันโอนเงิน</h3>
                                <p>ตั้งวันโอนเงินสำหรับลูกจ้าง/พนักงาน</p>
                            </div>
                        </Link>
                    </Can>

                    {canManageBlacklist && (
                        <Link to="/admin/banned-cids" className="admin-menu-card">
                            <div className="menu-icon" style={{ background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)' }}>
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zM4 12c0-4.42 3.58-8 8-8 1.85 0 3.55.63 4.9 1.69L5.69 16.9C4.63 15.55 4 13.85 4 12zm8 8c-1.85 0-3.55-.63-4.9-1.69L18.31 7.1C19.37 8.45 20 10.15 20 12c0 4.42-3.58 8-8 8z"/>
                                </svg>
                            </div>
                            <div className="menu-text">
                                <h3>บัญชีดำ (Blacklist)</h3>
                                <p>ระงับเลขบัตรไม่ให้เข้าระบบ</p>
                            </div>
                        </Link>
                    )}
                </div>
            </div>

            {/* Recent Activity Logs */}
            <Can permission="logs.view">
                <div className="admin-section">
                    <div className="section-header">
                        <h2>Activity Logs ล่าสุด</h2>
                        <Link to="/admin/activity-logs" className="view-all-link">
                            ดูทั้งหมด →
                        </Link>
                    </div>
                    <div className="steam-card">
                        <div className="steam-card-body" style={{ padding: 0 }}>
                            {recentLogs.length === 0 ? (
                                <div className="no-logs-message">
                                    <svg viewBox="0 0 24 24" width="48" height="48" fill="currentColor" opacity="0.3">
                                        <path d="M13 3c-4.97 0-9 4.03-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42C8.27 19.99 10.51 21 13 21c4.97 0 9-4.03 9-9s-4.03-9-9-9zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12z"/>
                                    </svg>
                                    <p>ยังไม่มีประวัติการใช้งาน</p>
                                </div>
                            ) : (
                                <div className="activity-log-list">
                                    {recentLogs.map(log => {
                                        const badge = getActionBadge(log.action);
                                        return (
                                            <div key={log.id} className="activity-log-item">
                                                <div className={`log-icon ${badge.class}`}>
                                                    {badge.icon}
                                                </div>
                                                <div className="log-content">
                                                    <div className="log-main">
                                                        <span className="log-user">{log.fullname || log.username || 'ไม่ระบุ'}</span>
                                                        <span className={`log-badge ${badge.class}`}>{badge.text}</span>
                                                    </div>
                                                    <div className="log-detail">
                                                        {log.action_detail || '-'}
                                                    </div>
                                                </div>
                                                <div className="log-actions">
                                                    <div className="log-time">
                                                        {formatTime(log.created_at)}
                                                    </div>
                                                    {log.user_id && (
                                                        <Link
                                                            to={`/admin/user-view/${log.user_id}`}
                                                            className="log-view-btn"
                                                            title="ดูข้อมูลผู้ใช้"
                                                        >
                                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" width="16" height="16">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                            </svg>
                                                            เนื้อสกรู
                                                        </Link>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </Can>

            {/* Modal: รายชื่อผู้ที่ยืนยันอีเมลแล้ว */}
            {emailModalOpen && (
                <div className="modal-overlay" onClick={() => setEmailModalOpen(false)}>
                    <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>ผู้ที่ยืนยันอีเมลแล้ว {!loadingVerified && `(${verifiedUsers.length})`}</h3>
                            <button className="modal-close" onClick={() => setEmailModalOpen(false)}>&times;</button>
                        </div>
                        <div className="modal-body" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                            {loadingVerified ? (
                                <div className="flex-center" style={{ padding: '40px' }}>
                                    <div className="spinner"></div>
                                </div>
                            ) : verifiedUsers.length === 0 ? (
                                <p style={{ textAlign: 'center', color: 'var(--steam-text-muted)', padding: '30px 0' }}>
                                    ยังไม่มีผู้ใช้ที่ยืนยันอีเมล
                                </p>
                            ) : (
                                <>
                                    <input
                                        type="text"
                                        className="steam-input"
                                        placeholder="ค้นหาชื่อ หรืออีเมล..."
                                        value={verifiedSearch}
                                        onChange={(e) => setVerifiedSearch(e.target.value)}
                                        style={{ marginBottom: 12 }}
                                    />
                                    <div className="verified-user-list">
                                        {verifiedUsers
                                            .filter(u => {
                                                const q = verifiedSearch.trim().toLowerCase();
                                                if (!q) return true;
                                                return (u.fullname || '').toLowerCase().includes(q)
                                                    || (u.email || '').toLowerCase().includes(q)
                                                    || (u.emp_code || '').toLowerCase().includes(q);
                                            })
                                            .map(u => (
                                                <Link
                                                    key={u.id}
                                                    to={`/admin/user-view/${u.id}`}
                                                    className="verified-user-item"
                                                    onClick={() => setEmailModalOpen(false)}
                                                >
                                                    <div className={`user-avatar-small ${u.avatar_url ? 'has-image' : ''}`}>
                                                        {u.avatar_url ? (
                                                            <img
                                                                src={`/api${u.avatar_url}`}
                                                                alt={u.fullname || 'avatar'}
                                                                onError={(e) => {
                                                                    e.target.style.display = 'none';
                                                                    e.target.parentNode.classList.remove('has-image');
                                                                    e.target.parentNode.textContent = u.fullname?.charAt(0) || '?';
                                                                }}
                                                            />
                                                        ) : (
                                                            u.fullname?.charAt(0) || '?'
                                                        )}
                                                    </div>
                                                    <div className="verified-user-info">
                                                        <div className="verified-user-name">
                                                            {u.fullname || 'ไม่ระบุชื่อ'}
                                                            {u.role_display && (
                                                                <span className="badge badge-primary" style={{ marginLeft: 8 }}>{u.role_display}</span>
                                                            )}
                                                        </div>
                                                        <div className="verified-user-email">{u.email || '-'}</div>
                                                    </div>
                                                    <svg className="verified-user-arrow" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                                    </svg>
                                                </Link>
                                            ))}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                .verified-user-list {
                    display: flex;
                    flex-direction: column;
                    gap: 6px;
                }
                .verified-user-item {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 10px 12px;
                    border-radius: 10px;
                    border: 1px solid var(--steam-border-light, #e5e7eb);
                    text-decoration: none;
                    color: inherit;
                    transition: all 0.15s ease;
                }
                .verified-user-item:hover {
                    background: var(--steam-accent-light, #eff6ff);
                    border-color: var(--steam-accent, #3b82f6);
                }
                .verified-user-info {
                    flex: 1;
                    min-width: 0;
                }
                .verified-user-name {
                    font-weight: 500;
                    color: var(--steam-text-primary, #1f2937);
                    display: flex;
                    align-items: center;
                    flex-wrap: wrap;
                }
                .verified-user-email {
                    font-size: 13px;
                    color: var(--steam-text-muted, #6b7280);
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
                .verified-user-arrow {
                    flex-shrink: 0;
                    color: var(--steam-text-muted, #94a3b8);
                }
                .verified-user-item:hover .verified-user-arrow {
                    color: var(--steam-accent, #3b82f6);
                }
            `}</style>
        </div>
    );
}

export default AdminDashboard;
